import React from 'react';
import { colorFromName } from '../utils/colorHash';

/**
 * SearchFilters
 * Sidebar filter panel.
 *
 * Layout (v1.0.4 — Region restored per Touch's request):
 *   1. Search input (pill, icon on right) — placeholder "ชื่อคลินิก"
 *   2. Products multi-select (colored radios — each product gets a deterministic
 *      color from src/utils/colorHash.js so Bienox/Fillstim/Volifil etc. always
 *      look distinct).
 *   3. Region dropdown (pill, "ทุกภูมิภาค" default) — drives province cascade.
 *   4. Province dropdown (pill, cascades on region). When no region is picked,
 *      a small subtle hint nudges the user to pick a region first.
 *   5. Big blue pill button → "ค้นหาจากตำแหน่งที่ตั้งของคุณ" (geolocation).
 *      HTTPS / GPS errors render as a SUBTLE gray hint underneath, never as a
 *      red flash.
 *   6. Outline pill button → "ล้างตัวกรอง" (always visible).
 */
export default function SearchFilters({
  regions,
  provinces,
  products,
  searchTerm,
  selectedRegion,
  selectedProvince,
  selectedProducts,
  onSearchChange,
  onRegionChange,
  onProvinceChange,
  onProductToggle,
  onClearFilters,
  nearMeActive,
  geoLoading,
  geoError,
  onNearMeToggle,
}) {
  const regionPicked =
    selectedRegion !== '' && selectedRegion !== 0 && selectedRegion !== '0';

  return (
    <div className="cs-filters">
      {/* ── Search input (pill + icon-right) ─────────────────────── */}
      <div className="cs-filter-group">
        <label className="cs-filters__section-label" htmlFor="cs-search">
          ค้นหา:
        </label>
        <div className="cs-search-input-wrap">
          <input
            id="cs-search"
            type="search"
            className="cs-search-input"
            placeholder="ชื่อคลินิก"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            autoComplete="off"
          />
          <span className="cs-search-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </span>
        </div>
      </div>

      {/* ── Products (multi-select, colored radios) ─────────────── */}
      {products.length > 0 && (
        <div className="cs-filter-group">
          <label className="cs-filters__section-label">
            ค้นหาจากผลิตภัณฑ์
            <span className="cs-filters__section-hint">(เลือกได้มากกว่า 1):</span>
          </label>
          <div className="cs-product-options">
            {products.map((product) => {
              const isChecked = selectedProducts.includes(product.id);
              const productColor = colorFromName(product.name);
              return (
                <label
                  key={product.id}
                  className={`cs-product-option${isChecked ? ' cs-product-option--checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="cs-product-input"
                    checked={isChecked}
                    onChange={() => onProductToggle(product.id)}
                  />
                  <span
                    className={`cs-product-radio${isChecked ? ' cs-product-radio--checked' : ''}`}
                    style={{
                      borderColor: productColor,
                      backgroundColor: isChecked ? productColor : 'transparent',
                    }}
                    aria-hidden="true"
                  >
                    {isChecked && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  <span className="cs-product-label">{product.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Region dropdown (pill) ──────────────────────────────── */}
      {/* Restored in v1.0.4. Drives province cascade in useClinicSearch — when
          region = 0 the province <select> shows all 77 provinces, otherwise it
          shows only provinces that belong to the picked region. */}
      <div className="cs-filter-group">
        <label className="cs-filters__section-label" htmlFor="cs-region">
          ภูมิภาค:
        </label>
        <select
          id="cs-region"
          className="cs-select"
          value={selectedRegion}
          onChange={(e) => onRegionChange(e.target.value)}
          disabled={!regions || regions.length === 0}
        >
          <option value="">ทุกภูมิภาค</option>
          {(regions || []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>

      {/* ── Province dropdown (pill, cascade on region) ─────────── */}
      <div className="cs-filter-group">
        <label className="cs-filters__section-label" htmlFor="cs-province">
          จังหวัด:
        </label>
        <select
          id="cs-province"
          className="cs-select"
          value={selectedProvince}
          onChange={(e) => onProvinceChange(e.target.value)}
          disabled={provinces.length === 0}
        >
          <option value="">ทุกจังหวัด</option>
          {provinces.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {!regionPicked && (
          <p className="cs-filter-hint">เลือกภูมิภาคก่อนเพื่อกรองจังหวัด</p>
        )}
      </div>

      {/* ── Geolocation: big blue pill ──────────────────────────── */}
      <div className="cs-filter-group">
        <button
          type="button"
          className={`cs-geo-btn${nearMeActive ? ' cs-geo-btn--active' : ''}`}
          onClick={onNearMeToggle}
          disabled={geoLoading}
          aria-pressed={nearMeActive}
        >
          {geoLoading ? (
            <span className="cs-geo-btn__spinner" aria-hidden="true" />
          ) : (
            <span className="cs-geo-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
              </svg>
            </span>
          )}
          <span>
            {geoLoading
              ? 'กำลังระบุตำแหน่ง...'
              : nearMeActive
              ? 'ใกล้ฉัน (เปิดอยู่)'
              : 'ค้นหาจากตำแหน่งที่ตั้งของคุณ'}
          </span>
        </button>
        {geoError && <p className="cs-geo-hint">{geoError}</p>}
      </div>

      {/* ── Clear filters: outline pill ─────────────────────────── */}
      <button
        type="button"
        className="cs-clear-btn-pill"
        onClick={onClearFilters}
      >
        ล้างตัวกรอง
      </button>
    </div>
  );
}
